// Presentation helpers for tasks-v2. Pure, no side effects.

import type { Priority, Profile, StageKey, Task } from "./types";
import { PROFILES, PROJECTS, SPRINTS, STAGES, TASK_TYPES } from "./data";

export const projectsById = new Map(PROJECTS.map((p) => [p.id, p]));
export const sprintsById = new Map(SPRINTS.map((s) => [s.id, s]));
export const typesById = new Map(TASK_TYPES.map((t) => [t.id, t]));
export const stagesByKey = new Map(STAGES.map((s) => [s.key, s]));
export const profilesById = new Map(PROFILES.map((p) => [p.id, p]));

export const PRIORITY_BORDER: Record<Priority, string> = {
	urgent: "border-l-danger",
	high: "border-l-warning",
	medium: "border-l-primary",
	low: "border-l-border-strong",
};

export const STATUS_VARIANT: Record<
	StageKey,
	"backlog" | "todo" | "in-progress" | "review" | "done" | "cancelled"
> = {
	backlog: "backlog",
	todo: "todo",
	"in-progress": "in-progress",
	qa: "review",
	done: "done",
	cancelled: "cancelled",
};

const AVATAR_COLORS = [
	"bg-primary",
	"bg-accent",
	"bg-secondary",
	"bg-warning",
	"bg-danger",
];

export function profileColorClass(id: string): string {
	const sum = id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
	return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export function getInitials(name: string | null | undefined): string {
	if (!name) return "?";
	return name
		.split(" ")
		.map((w) => w[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();
}

export function allAssignees(task: Task): Profile[] {
	return [task.assigned_to, ...task.additional_assignees].filter(
		(p): p is Profile => !!p,
	);
}

export function formatMinutes(minutes: number): string {
	if (!minutes) return "0 min";
	if (minutes === 1) return "1 min";
	if (minutes < 60) return `${minutes} mins`;
	const hours = Math.floor(minutes / 60);
	const rem = minutes % 60;
	if (rem === 0) return `${hours} hr${hours > 1 ? "s" : ""}`;
	return `${hours} hr${hours > 1 ? "s" : ""} ${rem} min`;
}

export function formatDueDate(iso: string | null): string {
	if (!iso) return "No due date";
	const date = new Date(`${iso.slice(0, 10)}T00:00:00`);
	const today = new Date("2026-07-21T00:00:00");
	const diff = Math.round(
		(date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
	);
	if (diff === 0) return "Today";
	if (diff === 1) return "Tomorrow";
	if (diff === -1) return "Yesterday";
	if (diff > 1 && diff <= 14) return `In ${diff} days`;
	if (diff < -1 && diff >= -14) return `${Math.abs(diff)} days ago`;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

export function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function priorityLabel(p: Priority): string {
	return p.charAt(0).toUpperCase() + p.slice(1);
}

export function checklistPct(task: Task): number {
	const total = task.checklist.length;
	if (total === 0) return 0;
	const done = task.checklist.filter((c) => c.done).length;
	return Math.round((done / total) * 100);
}
